import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

interface GymBranding {
  gymName: string;
  logoUrl: string | null;
  gymId: string | null;
}

interface GymBrandingContextType {
  branding: GymBranding | null;
  loading: boolean;
  refreshBranding: () => Promise<void>;
}

const GymBrandingContext = createContext<GymBrandingContextType | undefined>(undefined);

export const useGymBranding = () => {
  const context = useContext(GymBrandingContext);
  if (context === undefined) {
    throw new Error('useGymBranding must be used within a GymBrandingProvider');
  }
  return context;
};

interface GymBrandingProviderProps {
  children: ReactNode;
}

export const GymBrandingProvider: React.FC<GymBrandingProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [branding, setBranding] = useState<GymBranding | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchBranding = async () => {
    if (!user?.gymId) {
      setBranding(null);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(`/Gym/${user.gymId}`);
      const gym = response.data;
      setBranding({
        gymName: gym.gymName || 'GymsSync',
        logoUrl: gym.logoUrl || null,
        gymId: gym.gymId
      });
    } catch (error) {
      console.error('Error fetching gym branding:', error);
      // Fallback to default branding
      setBranding({
        gymName: 'GymsSync',
        logoUrl: null,
        gymId: user.gymId
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.gymId) {
      fetchBranding();
    } else {
      setBranding(null);
      setLoading(false);
    }
  }, [user?.gymId]);

  const refreshBranding = async () => {
    setLoading(true);
    await fetchBranding();
  };

  return (
    <GymBrandingContext.Provider value={{ branding, loading, refreshBranding }}>
      {children}
    </GymBrandingContext.Provider>
  );
};

